import { type ColumnOption } from "interfaces/columnOptions";
import { ContentColumns, type ContentItem, type FlowItem } from "../../types/models";
import { Tooltip } from "components/common/tooltip";
import { EditPriorityLinkButton, TextTruncate } from "components";
import { Title } from "components/listViewTemplates";
import { PublishStatus } from "components/common/PublishStatus";
import { type PriorityLevels, PublishedStatusTypes } from "enums";
import { ObjectiveLabel } from "features/Library/Flows/common/ObjectiveLabel/ObjectiveLabel";
import PriorityCell from "components/priorityLevels/priorityCell/PriorityCell";
import { ContentType, PeopleType } from "features/Library/PeopleAssignments/types";
import EllipsisPopupButton from "components/buttons/ellipsisPopupButton/EllipsisPopupButton";
import RemovePriorityButton from "components/buttons/linkButtons/RemovePriorityButton/RemovePriorityButton";
import { HiddenContent } from "components/HiddenContent/HiddenContent";

interface GetColumnOptionsParams {
  url: string;
  selectedIds: number[];
  contextMenuButtonHandlers: {
    onEditPriority: (item: FlowItem) => void;
    onClearPriority: (ids: number[]) => void;
  };
  disabled: boolean;
}

/* istanbul ignore next */
export const getFlowColumnOptions = (props: GetColumnOptionsParams): ColumnOption<FlowItem>[] => {
  const {
    url,
    selectedIds,
    contextMenuButtonHandlers: { onEditPriority, onClearPriority },
    disabled,
  } = props;
  return [
    {
      name: ContentColumns.Title,
      width: 5,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden} showIcon>
          <Title className="normal-color" title={item.title} url={!disabled ? `${url}/${item.id}` : undefined} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.Objective,
      width: 3,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden}>
          <ObjectiveLabel objective={item.goals?.objective} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.Publisher,
      width: 3,
      isSortable: false,
      render: (item) => (
        <Tooltip
          target={
            <TextTruncate>
              <HiddenContent variant="list" isHidden={item.hidden}>
                {item.publisher}
              </HiddenContent>
            </TextTruncate>
          }
          content={item.publisher}
        />
      ),
    },
    {
      name: ContentColumns.Status,
      width: 2,
      isSortable: false,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden}>
          <PublishStatus publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(item.published)} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.PriorityLevel,
      width: 3,
      isSortable: false,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden}>
          <PriorityCell
            inherited={item.isInherited}
            peopleType={PeopleType.Group}
            contentType={ContentType.Flow}
            priorityLevel={item.priorityLevelId as PriorityLevels}
            dueDate={item.dueDate}
            daysToComplete={item.daysToComplete}
          />
        </HiddenContent>
      ),
    },
    {
      name: "",
      width: 1,
      render: (item) => (
        <EllipsisPopupButton
          circle
          outlinedEllipsis
          disabled={item.isInherited || selectedIds.includes(item.id) || disabled}
        >
          <EditPriorityLinkButton onClick={() => onEditPriority(item)} />
          <RemovePriorityButton
            key="remove-priority-menu-button"
            onClick={() => onClearPriority([item.id])}
            isDisabled={!item.canRemove}
            context="people"
            entityType={ContentType.Flow}
            peopleType={PeopleType.Group}
            showTooltip={!item.canRemove}
            multiple={false}
          />
        </EllipsisPopupButton>
      ),
      className: "align-right",
    },
  ];
};

/* istanbul ignore next */
export const getColumnOptions = (url: string, disabled: boolean): ColumnOption<ContentItem>[] => {
  return [
    {
      name: ContentColumns.Title,
      width: 4,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility} showIcon>
          <Title className="normal-color" title={item.title} url={!disabled ? `${url}/${item.id}` : undefined} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.Publisher,
      width: 3,
      isSortable: false,
      render: (item) => (
        <Tooltip
          target={
            <TextTruncate>
              <HiddenContent variant="list" isHidden={!item.visibility}>
                {item.publisher}
              </HiddenContent>
            </TextTruncate>
          }
          content={item.publisher}
        />
      ),
    },
    {
      name: ContentColumns.Status,
      width: 6,
      isSortable: false,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          <PublishStatus publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!item.isDraft)} />
        </HiddenContent>
      ),
    },
  ];
};
